import { Link } from 'react-router-dom';
import styles from './Careers.module.scss';
import { useState, useEffect, useMemo } from 'react';
import { FaPaperPlane, FaChevronRight, FaCheckCircle, FaLightbulb, FaBalanceScale, FaBookOpen, FaHeart } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { HERO, PROMISES, GROW_WITH_US, JOIN_US, RESUME_FORM, SUCCESS_POPUP } from '../../content/careers';
import { NAP, PAGE_SEO } from '../../content/site';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';
import PageHero from '../../components/PageHero/PageHero';
// Imported rather than referenced as '/assets/grafics.jpg', which is what the
// old hero did -- there is no public/assets/ directory, so it 404'd (P0-9).
// This is the WebP of the same image, 1.1 MB against the JPEG's 12.5 MB.
import heroImage from '../../assets/careersHero.webp';

type JobListing = {
  name?: string;
  location?: string;
  postedDate?: string;
  remoteAllowed?: boolean;
  active?: boolean;
  jobId?: string;
  jobRoles?: string;
  description?: string;
};

const PROMISE_ICONS = {
  lightbulb: FaLightbulb,
  bookOpen: FaBookOpen,
  balanceScale: FaBalanceScale,
  heart: FaHeart,
} as const;

const CareersPage = () => {
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        position: '',
        customPosition: '',
        message: ''
    });
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    // The scroll cue and its handler moved into PageHero, which now owns both
    // for every page that has one.

    // ✅ JobListings API integration
    const [jobListings, setJobListings] = useState<JobListing[]>([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [jobsError, setJobsError] = useState<string | null>(null);

    // CRA env var
    const apiBase = useMemo(() => {
        const raw = process.env.REACT_APP_JOBLISTINGS_API || '';
        return raw.trim().replace(/\/$/, '');
    }, []);

    // Card selection state
    const [selectedPromise, setSelectedPromise] = useState<number | null>(null);

    // State for image loading errors
    const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});

    // ✅ Load Jobs from API
    useEffect(() => {
        if (!apiBase) {
            setJobsError('Missing environment variable: REACT_APP_JOBLISTINGS_API');
            return;
        }

        const controller = new AbortController();

        const loadJobs = async () => {
            try {
                setJobsLoading(true);
                setJobsError(null);

                const url = `${apiBase}/jobs`;
                const res = await fetch(url, {
                method: 'GET',
                signal: controller.signal
            });

            if (!res.ok) {
              const text = await res.text().catch(() => '');
              throw new Error(`Jobs API error (${res.status}): ${text || res.statusText}`);
            }

            const data = await res.json();
            const normalized =
              data?.body && typeof data.body === 'string'
                ? JSON.parse(data.body)
                : data;

            const items: JobListing[] = Array.isArray(normalized) ? normalized : [];
            const activeOnly = items.some(i => typeof i.active === 'boolean')
              ? items.filter(i => i.active)
              : items;

            setJobListings(activeOnly);
          } catch (err: any) {
            if (err?.name === 'AbortError') return;
            console.error('Failed to load jobs:', err);
            setJobsError(err?.message || 'Failed to load jobs');
          } finally {
            setJobsLoading(false);
          }
        };

        loadJobs();
        return () => controller.abort();
      }, [apiBase]);

      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            // If user selects "Other", allow them to specify a custom position
            ...(name === 'position' && value !== 'Other' ? { customPosition: '' } : {})
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) setResumeFile(e.target.files[0]);
    };

    // helper for file submissions
    const fileToBase64 = (file: File) =>
        new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(',')[1]); // Get base64 string without the prefix
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    // form submission handler
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // form validation: ensure required fields are filled
        const selectedPosition =
            formData.position === 'Other' ? formData.customPosition : formData.position;
            
        if (!formData.full_name || !formData.email || !selectedPosition || !resumeFile) {
            alert('Please fill in all required fields.');
            return;
        }

        setIsSubmitting(true);
        
        try {
            const payload = {
                full_name: formData.full_name,
                email: formData.email,
                phone: formData.phone,
                position: selectedPosition,
                message: formData.message,
                fileName: resumeFile.name,
                fileType: resumeFile.type,
                fileData: await fileToBase64(resumeFile),
            };
            
            const response = await fetch('https://mgp8qmdgn1.execute-api.us-east-2.amazonaws.com/apply', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            
            const result = await response.json();
            
            if (response.ok && result.success) {
                setShowSuccessPopup(true);
                setFormData({
                    full_name: '',
                    email: '',
                    phone: '',
                    position: '',
                    customPosition: '',
                    message: '',
                });
                setResumeFile(null);
                setTimeout(() => setShowSuccessPopup(false), 5000);
            } else {
                throw new Error(result.error || 'Submission failed');
            }
        } catch (err) {
            console.error(err);
            alert('There was an error submitting your application. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // Helper: job title
    const jobTitle = (job: JobListing) => job.name || job.jobRoles || 'Open Position';

    // Helper: stable key
    const jobKey = (job: JobListing, idx: number) => job.jobId || job.name || job.jobRoles || `job-${idx}`;

    // Check for success query param to show popup
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        
        if (params.get('submitted') === 'true') {
            setShowSuccessPopup(true);
            
            const newUrl = `${window.location.pathname}`;
            window.history.replaceState({}, '', newUrl);
            
            const timer = window.setTimeout(() => setShowSuccessPopup(false), 5000);
            return () => window.clearTimeout(timer);
        }
    }, []);


    return (
        <div className={styles.careersPage}>
            <Seo {...seoPropsFor(PAGE_SEO.careers)} />

            {showSuccessPopup && (
                <div className={styles.successPopup}>
                    <div className={styles.popupContent}>
                        <FaCheckCircle className={styles.successIcon} />
                        <h3>{SUCCESS_POPUP.heading}</h3>
                        <p>{SUCCESS_POPUP.body}</p>
                        <p>
                            {SUCCESS_POPUP.followUp} <MdEmail />{' '}
                            <a href={`mailto:${NAP.email}`}>{NAP.email}</a>.
                        </p>
                        <button onClick={() => setShowSuccessPopup(false)} className={styles.closeButton}>
                            {SUCCESS_POPUP.closeLabel}
                        </button>
                    </div>
                </div>
            )}

            <div className={isVisible ? 'main-content' : ''}>
                <PageHero
                  title={HERO.title}
                  subtitle={HERO.subtitle}
                  image={heroImage}
                  tone="careers"
                  showScrollHint
                />

                {/* Grow with Us Section */}
                <section className={styles.growWithUs} id="growWithUs">
                    <div className={styles.container}>
                        <div className={styles.sectionHeader}>
                            <h2>{GROW_WITH_US.heading}</h2>
                        </div>
                        <div className={styles.growContent}>
                            {/* Promise Cards */}
                            <div className={styles.promiseCards}>
                                {PROMISES.map(promise => {
                                    const Icon = PROMISE_ICONS[promise.iconKey];
                                    return (
                                        <div
                                        key={promise.id}
                                        className={`${styles.promiseCard} ${selectedPromise == promise.id ? styles.selected : ''}`}
                                        onClick={() => setSelectedPromise(selectedPromise == promise.id ? null : promise.id)}>
                                            <Icon className={styles.promiseIcon} />
                                            <h3>{promise.title}</h3>
                                            <p>{promise.description}</p>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Link to About */}
                            <div className={styles.aboutBlock}>
                                <h3>{GROW_WITH_US.aboutBlock.heading}</h3>
                                <p>{GROW_WITH_US.aboutBlock.body}</p>
                                <Link to={GROW_WITH_US.aboutBlock.ctaHref} className={`${styles.btn} ${styles['btn-primary']}`}>{GROW_WITH_US.aboutBlock.ctaLabel}</Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Join Us Section */}
                <section className={styles.joinUs} id="joinUs">
                    <div className={styles.container}>
                        <div className={styles.sectionHeader}>
                            <h2>{JOIN_US.heading}</h2>
                            <p>{JOIN_US.subtitle}</p>
                        </div>
                        <div className={styles.joinContent}>
                            <div className={styles.opportunities}>
                                <h3><FaChevronRight className={styles.icon} /> {JOIN_US.openingsHeading}</h3>

                                {jobsLoading && <p>Loading open positions...</p>}

                                {jobsError && (
                                    <div style={{ marginBottom: 12 }}>
                                        <p style={{ fontWeight: 600 }}>Could not load positions from the database.</p>
                                        <p style={{ opacity: 0.85, fontSize: 14 }}>{jobsError}</p>
                                    </div>
                                )}

                                {!jobsLoading && !jobsError && jobListings.length > 0 ? (
                                    <ul className={styles.positionsList}>
                                        {jobListings.map((job, idx) => {
                                            // Location and work arrangement share one parenthetical.
                                            // A missing remoteAllowed says nothing either way, so it
                                            // contributes nothing rather than defaulting to "On-site".
                                            const meta = [
                                                job.location,
                                                typeof job.remoteAllowed === 'boolean'
                                                    ? job.remoteAllowed
                                                        ? 'Remote allowed'
                                                        : 'On-site'
                                                    : null,
                                            ].filter(Boolean);

                                            return (
                                                <li key={jobKey(job, idx)}>
                                                    <FaChevronRight className={styles.bulletIcon} />
                                                    <strong>{jobTitle(job)}</strong>
                                                    {meta.length > 0 ? (
                                                        <span style={{ opacity: 0.85 }}> ({meta.join(' · ')})</span>
                                                    ) : null}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : null}

                                {!jobsLoading && !jobsError && jobListings.length === 0 && (
                                    <p style={{ opacity: 0.9 }}>
                                        {JOIN_US.emptyJobsMessage}
                                    </p>
                                )}

                                <div className={styles.directContact}>
                                    <h4>{JOIN_US.directContact.heading}</h4>
                                    <p>{JOIN_US.directContact.body} <MdEmail /> <a href={`mailto:${NAP.email}`}>{NAP.email}</a></p>
                                    </div>
                                </div>
                                
                                <div className={`${styles.resumeForm} text-rise`}>
                                    <h3><FaPaperPlane className={styles.icon} /> Submit Your Resume</h3>
                                    
                                    <form id="careers-form" onSubmit={handleSubmit}>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="full_name" style={{ display: 'none' }}>Full Name</label>
                                            <input
                                            id="full_name"
                                            type="text"
                                            name="full_name"
                                            placeholder="Full Name"
                                            value={formData.full_name}
                                            onChange={handleInputChange}
                                            required
                                            autoComplete="name"
                                        />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label htmlFor="email" style={{ display: 'none' }}>Email</label>
                                            <input
                                            id="email"
                                            type="email"
                                            name="email"
                                            placeholder="Email Address"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            autoComplete="email"
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="phone" style={{ display: 'none' }}>Phone</label>
                                        <input
                                            id="phone"
                                            type="tel"
                                            name="phone"
                                            placeholder="Phone Number"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            autoComplete="tel"
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="position" style={{ display: 'none' }}>Position</label>
                                        <select
                                            id="position"
                                            name="position"
                                            value={formData.position}
                                            onChange={handleInputChange}
                                            required
                                            autoComplete="off"
                                        >
                                            <option value="" disabled hidden>Select Position</option>

                                            {jobListings.length > 0 ? (
                                                <>
                                                {jobListings.map((job, idx) => {
                                                    const title = jobTitle(job);
                                                    return (
                                                        <option key={`${title}-${idx}`} value={title}>
                                                            {title}
                                                        </option>
                                                    );
                                                })}
                                                <option value="Other">Other</option>
                                                </>
                                            ) : (
                                             <>
                                                {RESUME_FORM.fallbackPositions.map((position) => (
                                                    <option key={position} value={position}>{position}</option>
                                                ))}
                                                <option value="Other">Other</option>
                                            </>
                                        )}
                                    </select>

                                    {formData.position ==='Other' && (
                                        <div className={styles.formGroup}>
                                            <label htmlFor="customPosition" style={{ display: 'none '}}>Custom Position</label>
                                            <input
                                                id="customPosition"
                                                type="text"
                                                name="customPosition"
                                                placeholder="Enter custom position"
                                                value={formData.customPosition}
                                                onChange={handleInputChange}
                                                required
                                                autoComplete="off"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="resume">Upload Resume (PDF or DOCX)</label>
                                    <input
                                        type="file"
                                        id="resume"
                                        name="resume"
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleFileChange}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="message" style={{ display: 'none' }}>Message</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        placeholder="Cover Letter or Additional Information"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <button type="submit" className={`${styles.btn} ${styles.primary}`} disabled={isSubmitting}>
                                    {isSubmitting ? RESUME_FORM.submittingLabel : <><FaPaperPlane /> {RESUME_FORM.submitLabel}</>}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
  );
};

export default CareersPage;
